// SPDX-License-Identifier: AGPL-3.0-or-later

export interface RoomOneshotTiming {
	minS: number;
	maxS: number;
}

export interface RoomOneshots {
	dispose(): void;
}

// Now-and-then room sounds (an owl, a log cracking): each group plays every minS–maxS seconds, all of them on one
// timer set for the next one due. A hidden tab has no timer; showing again draws every gap afresh, so nothing piles up.
export function createRoomOneshots<Group extends RoomOneshotTiming>({
	groups,
	play,
	random = Math.random,
}: {
	groups: ReadonlyArray<Group>;
	play: (group: Group, index: number) => void;
	random?: () => number;
}): RoomOneshots {
	const due = groups.map(() => 0);
	let timer: ReturnType<typeof setTimeout> | null = null;

	const gapMs = ({minS, maxS}: Group): number => (minS + random() * (maxS - minS)) * 1000;
	const stop = (): void => {
		if (timer !== null) clearTimeout(timer);
		timer = null;
	};
	const schedule = (): void => {
		stop();
		if (groups.length === 0 || document.hidden) return;
		timer = setTimeout(tick, Math.max(0, Math.min(...due) - Date.now()));
	};
	const tick = (): void => {
		timer = null;
		const now = Date.now();
		groups.forEach((group, index) => {
			if (due[index]! > now) return;
			play(group, index);
			due[index] = now + gapMs(group);
		});
		schedule();
	};
	const restart = (): void => {
		const now = Date.now();
		groups.forEach((group, index) => {
			due[index] = now + gapMs(group);
		});
		schedule();
	};
	const onVisibility = (): void => (document.hidden ? stop() : restart());

	if (groups.length > 0) document.addEventListener('visibilitychange', onVisibility);
	restart();

	return {
		dispose() {
			stop();
			document.removeEventListener('visibilitychange', onVisibility);
		},
	};
}
